import { Button, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form5498QAInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="2025 ABLE Account Form 5498-QA Instructions"
          description="File IRS Form 5498-QA for ABLE account contributions and rollovers. Follow 2025 instructions for compliance, deadlines, and e-filing tips."
          keywords=""
          canonical="resources/information-returns/form-5498-qa-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          IRS Form{' '}
          <span className="highlight-container">
            <span className="highlight"> 5498-QA</span>
          </span>{' '}
          Instructions
          <br />
          Complete Guide for ABLE Account Contributions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Quickly and accurately file IRS Form 5498-QA and maintain full
          compliance with IRS rules for ABLE accounts.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498QAinstruction`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-5498-qa-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 5498-QA at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Form 5498-QA
                        </strong>{' '}
                        reports
                        {
                          ' contributions, rollovers, transfers, and the year-end '
                        }
                        fair market value
                        {' of '}
                        <strong style={{ fontWeight: 600 }}>
                          ABLE (Achieving a Better Life Experience) accounts
                        </strong>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {'Recipient copies must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>March 15</strong>
                        {
                          ' following the calendar year being reported. The IRS filing deadline is '
                        }
                        <strong style={{ fontWeight: 600 }}>May 31</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>June 1</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {
                          'This article covers line-by-line instructions, including ABLE program information and designated beneficiary details, '
                        }
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>
                        {' (ABLE Contributions)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>
                        (Rollover
                        <strong style={{ fontWeight: 600 }}> </strong>
                        Contributions)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 3 '}</strong>
                        (Cumulative Contributions)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>
                        (Fair Market Value of the Account) ,{' '}
                        <strong style={{ fontWeight: 600 }}>Box 5</strong>
                        {' (Account Opened Checkbox), '}
                        <strong style={{ fontWeight: 600 }}>Box 6</strong>
                        {' (Basis of Eligibility), and '}
                        <strong style={{ fontWeight: 600 }}>{'Box 7 '}</strong>
                        (Type of Disability Code).
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 5498-QA
                        </strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the filer meets electronic filing obligations.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}> Form 5498-QA</span> is used
                  to report contributions, rollovers, and transfers made to an
                  Achieving a Better Life Experience (ABLE) savings account
                  during the tax year. This form is filed by the ABLE program
                  (the issuer) and provided to the account’s designated
                  beneficiary and the IRS. Beneficiaries use this form for
                  informational purposes only and do not file it with their tax
                  return.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What Is Form 5498-QA?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Each year, ABLE program administrators complete Form 5498-QA
                  to report the total contributions made to an individual’s ABLE
                  account, as well as any rollovers or program-to-program
                  transfers. The form also shows the account’s fair market value
                  (FMV) at year-end and identifies the basis of the
                  beneficiary’s eligibility for the account.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 5498-QA helps the IRS and account holders ensure that
                  contributions stay within the annual ABLE contribution limits
                  set under section 529A of the Internal Revenue Code.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Receives Form 5498-QA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Beneficiaries of ABLE savings accounts receive{' '}
                  <span style={{ fontWeight: 600 }}> Copy B</span> of Form
                  5498-QA from the program administrator. This copy is for your
                  personal records and includes key details about contributions
                  and account activity for the tax year.
                </Typography>
                {/* <Box
                  component={'div'}
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Simplify your 5498-SA process for HSA, Archer, and
                        Medicare MSA account details.
                      </Typography>
                      <Typography variant="body2">
                        E-file Form 5498-SA with TaxZerone for fast, accurate
                        reporting today.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498SAinstructions`}
                        id="who"
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Key Boxes Explained
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Box 1 – ABLE Contributions:
                    </span>{' '}
                    Shows the total cash contributions made to your ABLE account
                    in 2025, including rollovers from section 529 Qualified
                    Tuition Programs (QTPs). These contributions are not
                    deductible on your income tax return.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/f5498qa-able-contri-img.webp" />
                    <source srcSet="/assets/images/f5498qa-able-contri-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f5498qa-able-contri-img.png"
                      loading="lazy"
                      alt="Box 1 – ABLE Contributions:"
                      width={216}
                      height={79}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Box 2 – ABLE to ABLE Rollovers:
                    </span>{' '}
                    Reports rollovers or transfers made to this ABLE account
                    from another ABLE account for the same beneficiary or a
                    qualifying family member.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/f5498qa-able-rollover-img.webp" />
                    <source srcSet="/assets/images/f5498qa-able-rollover-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f5498qa-able-rollover-img.png"
                      loading="lazy"
                      alt="Box 1 – ABLE Contributions:"
                      width={217}
                      height={79}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Box 3 – Cumulative Contributions:
                    </span>{' '}
                    May show the total contributed amount since the account was
                    first opened, including transferred balances.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/f5498qa-cumulative-img.webp" />
                    <source srcSet="/assets/images/f5498qa-cumulative-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f5498qa-cumulative-img.png"
                      loading="lazy"
                      alt="Box 3 – Cumulative Contributions"
                      width={216}
                      height={54}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Box 4 – Fair Market Value:
                    </span>{' '}
                    May show the total contributed amount since the account was
                    first opened, including transferred balances.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/f5498qa-market-value-img.webp" />
                    <source srcSet="/assets/images/f5498qa-market-value-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f5498qa-market-value-img.png"
                      loading="lazy"
                      alt="Box 4 – Fair Market Value:"
                      width={216}
                      height={54}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Box 4 – Fair Market Value:
                    </span>{' '}
                    May show the total contributed amount since the account was
                    first opened, including transferred balances.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/f5498qa-market-value-img.webp" />
                    <source srcSet="/assets/images/f5498qa-market-value-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f5498qa-market-value-img.png"
                      loading="lazy"
                      alt="Box 4 – Fair Market Value:"
                      width={216}
                      height={54}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Box 5 – Account Opened in 2025:
                    </span>{' '}
                    This box is checked if the ABLE account was newly opened in
                    the 2025 tax year.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/f5498qa-account-img.webp" />
                    <source srcSet="/assets/images/f5498qa-account-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f5498qa-account-img.png"
                      loading="lazy"
                      alt="Box 5 – Account Opened in 2025"
                      width={217}
                      height={78}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Box 6 – Basis of Eligibility:
                    </span>{' '}
                    Identifies how the beneficiary qualifies for the ABLE
                    program:
                  </li>
                  <ul>
                    <li>A – Disability benefits under SSDI, Title II SSA </li>
                    <li>B – SSI eligibility, Title XVI SSA </li>
                    <li>
                      C – Disability certification under section 529A(e)(1)(B)
                    </li>
                  </ul>
                  <picture>
                    <source srcSet="/assets/images/f5498qa-basic-elig-img.webp" />
                    <source srcSet="/assets/images/f5498qa-basic-elig-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f5498qa-basic-elig-img.png"
                      loading="lazy"
                      alt="Box 5 – Account Opened in 2025"
                      width={217}
                      height={78}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                  <li>
                    Box 7 – requires ABLE program administrators to enter one
                    primary code that best describes the main disability for
                    which the beneficiary qualifies for ABLE benefits. Choose
                    the most significant condition if multiple apply. This helps
                    the IRS track ABLE program usage by disability type.
                  </li>
                  <picture>
                    <source srcSet="/assets/images/f5498qa-code-img.webp" />
                    <source srcSet="/assets/images/f5498qa-code-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f5498qa-code-img.png"
                      loading="lazy"
                      alt="Box 5 – Account Opened in 2025"
                      width={217}
                      height={52}
                      className="img-responsive"
                      sx={{
                        border: '2px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 21, md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Disability Code Breakdown
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Code 1 – Developmental Disorders
                    </span>{' '}
                    <br />
                    Covers conditions like autism spectrum disorder,
                    Asperger&apos;s syndrome, developmental delays, and learning
                    disabilities that affect early childhood growth and
                    skills.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Code 2 – Intellectual Disability
                    </span>{' '}
                    <br />
                    Includes mild, moderate, or severe intellectual disabilities
                    that impact cognitive functioning and adaptive behaviors.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Code 3 – Psychiatric Disorders
                    </span>{' '}
                    <br />
                    Encompasses mental health conditions such as schizophrenia,
                    major depression, PTSD, anorexia nervosa, ADHD, and bipolar
                    disorder.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Code 4 – Nervous System Disorders
                    </span>{' '}
                    <br />
                    Reports neurological and sensory conditions including
                    blindness, deafness, cerebral palsy, muscular dystrophy,
                    spina bifida, multiple sclerosis, juvenile Huntington&apos;s
                    disease, severe hearing loss, and congenital cataracts.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Code 5 – Congenital Birth Defects
                    </span>{' '}
                    <br />
                    Includes genetic conditions like Down syndrome, chromosomal
                    abnormalities, osteogenesis imperfecta, spinal muscular
                    atrophy, Fragile X syndrome, and Edwards syndrome.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Code 6 – Respiratory Conditions
                    </span>{' '}
                    <br />
                    Specifically covers cystic fibrosis and similar chronic lung
                    diseases.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Code 7 – All Other Disabilities
                    </span>{' '}
                    <br />
                    Use for any condition not covered by codes 1-6, such as
                    Tetralogy of Fallot, hypoplastic left heart syndrome,
                    end-stage liver disease, juvenile rheumatoid arthritis,
                    sickle cell disease, hemophilia, or other significant
                    disabilities.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important Contribution Limits
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  For 2025, the annual ABLE contribution limit is generally
                  $19,000. Employed beneficiaries may be able to contribute
                  additional earned income, subject to IRS limits under section
                  529A(b)(2)(B)(ii). If contributions exceed the allowable
                  amount, the excess and related earnings must be withdrawn by
                  the tax return due date to avoid penalties.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What to Do with Form 5498-QA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 5498-QA is for your records and is not filed with your
                  federal tax return. Keep this document to help verify your
                  ABLE account contributions and eligibility. It may also assist
                  when monitoring limits or preparing future tax filings.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to Get More Information?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For the most up-to-date instructions and reporting guidance,
                  visit the IRS page for{' '}
                  <Hidden smDown>
                    {' '}
                    <br />
                  </Hidden>
                  <Link
                    href="https://www.irs.gov/Form5498QA"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 5498-QA
                  </Link>
                  . Refer to IRS Publication 907 for additional details on tax
                  rules regarding disability-related savings.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Frequently Asked Questions (FAQs)
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. Who must submit Form 5498-QA to the IRS?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The responsibility to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498-qa/ "
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 5498-QA
                  </Link>{' '}
                  lies with the state or its authorized agency that manages the
                  ABLE savings program. This form must be filed by June 1st of
                  the year following the tax year reported.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. What is the electronic filing requirement for Form 5498-QA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have 10 or more ABLE accounts to report, you are
                  required to submit Form 5498-QA electronically to the IRS.
                  This requirement helps streamline filing and ensures timely
                  processing.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. Is there an electronic fillable version of Form 5498-QA
                  available?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes, the IRS provides an online fillable PDF version for
                  smaller filers to prepare and distribute statements, but bulk
                  filings to the IRS must comply with the mandatory e-filing
                  thresholds.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. How should errors or corrections on Form 5498-QA be
                  handled?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If any mistakes are found after filing, corrected forms must
                  be submitted as soon as possible to maintain accurate records
                  with the IRS. Guidelines are included in the{' '}
                  <Link
                    href="https://www.irs.gov/instructions/i1099gi"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS General Instructions for Information Returns
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  5. Are waivers available for e-filing Form 5498-QA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filers who face exceptional difficulty in e-filing may apply
                  for a waiver, but such exceptions are rarely approved as the
                  IRS strongly encourages electronic submissions.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  6. What software options exist for filing Form 5498-QA
                  electronically?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Several IRS-approved tax information reporting software
                  solutions support Form 5498-QA{' '}
                  <Hidden smDown>
                    {' '}
                    <br />
                  </Hidden>{' '}
                  e-filing, allowing program administrators to efficiently
                  prepare, review, and transmit files to the IRS.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to E-file your Form 5498-QA?
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              TaxZerone makes it simple, secure, and affordable to file your
              Form 5498-QA with the IRS.
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              Avoid delays, meet the IRS filing deadline, and stay fully
              compliant{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              with ABLE account reporting requirements.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498QAinstructions`}
              >
                E-file Your Form 5498-QA Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form5498QAInstructions;
